/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  createColumnHelper, flexRender, getCoreRowModel, useReactTable,
} from "@tanstack/react-table";
import { ArrowsUpDownIcon, PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

import type { ReportConfig, ReportFetchParams } from "./masterConfigs";

const getRowId = (row: any) => String(row.id);

interface IReportTableProps {
  config: ReportConfig;
  data: any[];
  totalRecords: number;
  onDataUpdate: (data: any[]) => void;
  fetchData: (params?: ReportFetchParams) => void;
  isLoading?: boolean;
}

type SelectedRowIds = Record<string, boolean>;

const ReportTable = ({
  config, data, totalRecords, onDataUpdate, fetchData, isLoading = false,
}: IReportTableProps) => {
  const { columns: columnConfig, fetchFn: _fetchFn, deleteFn, ModalComponent } = config;

  const columnKeys = useMemo(() => columnConfig.map((c) => c.key), [columnConfig]);
  const headerByKey = useMemo(
    () => Object.fromEntries(columnConfig.map((c) => [c.key, c.header])),
    [columnConfig]
  );
  const filterOperatorByKey = useMemo(
    () => Object.fromEntries(columnConfig.map((c) => [c.key, c.filterOperator])),
    [columnConfig]
  );

  const safeData = Array.isArray(data) ? data : [];

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" }>({
    key: columnKeys[0] || "id",
    direction: "asc",
  });
  const [selectedRowIds, setSelectedRowIds] = useState<SelectedRowIds>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [selectedData, setSelectedData] = useState<object | null>();

  const filterDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const columnHelper = createColumnHelper<any>();

  const escapeSql = (value: string) => value.replace(/'/g, "''");

  const buildSearchString = (filters: Record<string, string>) =>
    Object.entries(filters)
      .filter(([, value]) => value.trim() !== "")
      .map(([key, value]) => {
        const safeValue = escapeSql(value.trim());
        return filterOperatorByKey[key] === "ILIKE"
          ? `${key} ILIKE '%${safeValue}%'`
          : `${key} = '${safeValue}'`;
      })
      .join(" AND ");

  const loadData = (
    page: number, pageSize: number,
    orderbycol = sortConfig.key, direction = sortConfig.direction,
    filters = filterValues
  ) => {
    const searchstring = buildSearchString(filters);
    void fetchData({
      searchstring,
      orderbycol,
      orderdirection: direction === "asc" ? "ASC" : "DESC",
      startrowindex: page * pageSize + 1,
      maximumrows: pageSize,
      gettotal: searchstring.trim() === "",
    });
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = Number(e.target.value);
    setCurrentPage(0);
    setRowsPerPage(newRowsPerPage);
    loadData(0, newRowsPerPage);
  };

  const handleAddNewRecord = () => {
    setCurrentPage(0);
    fetchData()
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    loadData(newPage, rowsPerPage);
  };

  const requestSort = (key: string) => {
    const direction: "asc" | "desc" =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    setCurrentPage(0);
    loadData(0, rowsPerPage, key, direction);
  };

  const handleFilterChange = (columnKey: string, value: string) => {
    const updatedFilters = { ...filterValues, [columnKey]: value };
    setFilterValues(updatedFilters);
    setCurrentPage(0);
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      loadData(0, rowsPerPage, sortConfig.key, sortConfig.direction, updatedFilters);
    }, 500);
  };

  useEffect(() => () => {
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
  }, []);

  const handleExportToExcel = () => {
    const selectedIds = Object.keys(selectedRowIds);
    const selected = safeData.filter((row) => selectedIds.includes(getRowId(row)));
    if (selected.length === 0) {
      toast.error("No rows selected for export.", { theme: "colored" });
      return;
    }
    const formattedRows = selected.map((row) =>
      Object.fromEntries(columnConfig.map((c) => [c.header, row[c.key]]))
    );
    const worksheet = XLSX.utils.json_to_sheet(formattedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, config.title);
    XLSX.writeFile(workbook, config.exportFileName || `${config.title}.xlsx`);
  };

  const deleteRows = async (ids: string[], confirmMessage: string) => {
    if (!deleteFn) return;
    if (ids.length === 0) {
      toast.error("No rows selected for deletion.", { theme: "colored" });
      return;
    }
    if (!window.confirm(confirmMessage)) return;

    const response = await deleteFn(ids);
    const responseRows = (response.data as any)?.data || [];
    const succeeded =
      response.status &&
      (responseRows.length === 0 ||
        responseRows.every((row: any) => row.deletecustomer === "Success"));

    if (!succeeded) {
      toast.error("Delete failed.", { theme: "colored" });
      return;
    }
    toast.success(
      ids.length > 1 ? "Row(s) deleted successfully." : "Row deleted successfully.",
      { theme: "colored" }
    );
    onDataUpdate(safeData.filter((row) => !ids.includes(getRowId(row))));
    setSelectedRowIds((prev) => {
      const updated = { ...prev };
      ids.forEach((id) => delete updated[id]);
      return updated;
    });
  };

  const deleteAll = () => {
    const idsToDelete = Object.keys(selectedRowIds);
    void deleteRows(idsToDelete, `Are you sure you want to delete ${idsToDelete.length} row(s)?`);
  };

  const columns = useMemo(() => {
    const allSelected = safeData.length > 0 && Object.keys(selectedRowIds).length === safeData.length;

    const toggleRow = (rowId: string) => {
      setSelectedRowIds((prev) => {
        const updated = { ...prev };
        if (updated[rowId]) delete updated[rowId]; else updated[rowId] = true;
        return updated;
      });
    };

    const toggleAll = () => {
      if (allSelected) { setSelectedRowIds({}); return; }
      const selected: SelectedRowIds = {};
      safeData.forEach((row) => { selected[getRowId(row)] = true; });
      setSelectedRowIds(selected);
    };

    const createHeader = (key: string) => (
      <div className="p-2">
        <div className="flex flex-col gap-2 justify-center">
          <div className="flex gap-2 justify-center">{headerByKey[key] || key}</div>
          <div className="flex items-center justify-center gap-2">
            <input
              type="text"
              value={filterValues[key] || ""}
              onChange={(e) => handleFilterChange(key, e.target.value)}
              className="border w-[140px] border-gray-300 rounded px-2 py-1"
              disabled={isLoading}
            />
            <button onClick={() => requestSort(key)} disabled={isLoading}>
              <ArrowsUpDownIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );

    const baseCols: any[] = [
      {
        id: "select",
        header: <input type="checkbox" checked={allSelected} onChange={toggleAll} />,
        cell: (props: any) => {
          const rowId = getRowId(props.row.original);
          return (
            <input
              type="checkbox"
              checked={selectedRowIds[rowId] || false}
              onChange={() => toggleRow(rowId)}
            />
          );
        },
      },
    ];

    if (ModalComponent) {
      baseCols.push({
        id: "edit",
        header: <div className="p-2">Edit</div>,
        cell: (props: any) => (
          <div
            className="flex items-center justify-center gap-2 cursor-pointer"
            onClick={() => {
              setSelectedData(() => props.row.original);
              setModalMode("edit");
              setIsModalOpen(true);
            }}
          >
            <PencilIcon className="h-4 w-4 text-blue-500" />
          </div>
        ),
      });
    }

    if (deleteFn) {
      baseCols.push({
        id: "delete",
        header: <div className="p-2">Delete</div>,
        cell: (props: any) => (
          <div
            className="flex items-center justify-center gap-2 opacity-40 disabled:cursor-not-allowed"
            // onClick={() =>
            //   void deleteRows([getRowId(props.row.original)], "Are you sure you want to delete this row?")
            // }
          >
            <TrashIcon className="h-4 w-4 text-gray-500 disabled:cursor-not-allowed" />
          </div>
        ),
      });
    }

    return [
      ...baseCols,
      ...columnKeys.map((key) =>
        columnHelper.accessor(key, {
          header: createHeader(key) as any,
          cell: (props: any) => {
            const value = props.getValue();
            if (typeof value === "boolean") return <div>{value ? "Yes" : "No"}</div>;
            return <div>{value}</div>;
          },
        })
      ),
    ];
  }, [safeData, selectedRowIds, sortConfig, filterValues, isLoading, columnConfig]);

  const tableObj = useReactTable({
    columns: columns as any,
    data: safeData,
    enableRowSelection: true,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / rowsPerPage));

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <div>
          <label>
            Rows per page:
            <select value={rowsPerPage} onChange={handleRowsPerPageChange} className="ml-2 border border-gray-300 rounded" disabled={isLoading}>
              {[10, 50, 100, 250, 500].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
              <option disabled value={totalRecords}>All</option>
            </select>
          </label>
        </div>

        <div className="flex gap-2">
          {ModalComponent && (
            <button
              onClick={() => { setIsModalOpen(true); setModalMode("add"); }}
              className="flex items-center gap-1 bg-green-500 text-white text-sm py-1 px-3 rounded hover:bg-green-600"
              disabled={isLoading}
            >
              <PlusIcon className="h-4 w-4" /> New
            </button>
          )}

          <button onClick={handleExportToExcel} className="bg-orange-400 text-white py-1 px-2 rounded mr-1" disabled={isLoading}>
            Export to Excel
          </button>

          {deleteFn && (
            <button
              onClick={deleteAll}
              className="bg-red-500 text-white text-sm py-1 px-3 rounded hover:bg-red-600 disabled:opacity-25 disabled:cursor-not-allowed"
              disabled={true || Object.keys(selectedRowIds).length === 0}
            >
              Delete Selected
            </button>
          )}
        </div>
      </div>

      <div className={`w-full overflow-x-auto ${isLoading ? "opacity-50 pointer-events-none" : ""}`}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {tableObj.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th className="p-2" key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableObj.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border border-neutral-300">
                {row.getVisibleCells().map((cell) => (
                  <td className="border p-2 text-center" key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4">
        <div className="p-4">Showing {safeData.length} of {totalRecords || 0} entries</div>
        <div className="flex items-center gap-2">
          <button onClick={() => handlePageChange(Math.max(currentPage - 1, 0))} disabled={currentPage === 0 || isLoading} className="border border-gray-300 px-4 py-2 rounded mr-2 disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
          <span>Page {currentPage + 1} of {totalPages}</span>
          <button onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))} disabled={currentPage >= totalPages - 1 || isLoading} className="border border-gray-300 px-4 py-2 rounded ml-2 disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
        </div>
      </div>

      {isModalOpen && ModalComponent && (
        <ModalComponent
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSuccess={() => {
            handleCloseModal();
            modalMode === "add" ? handleAddNewRecord() : loadData(currentPage, rowsPerPage);
          }}
          mode={modalMode}
          selectedData={selectedData}
          config={config}
        />
      )}
    </div>
  );
};

export default ReportTable;